import { MenuItemProps, Tab } from 'semantic-ui-react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { usePermissions } from '@britishcouncil/react-common';
import { useGetExaminerQuery } from 'store';
import { useExaminerId } from './examiner';
import styled from '@emotion/styled';

type TabConfig = {
  menuItem: MenuItemProps;
  path: string;
  requiresAnyPermission?: Array<string | number>;
  requiresAnyRole?: string[];
  hidden?: boolean;
};

export const RoutedTabs = ({ tabsConfig, flex }: { tabsConfig: TabConfig[]; flex?: boolean }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const examinerId = useExaminerId();
  const { permissions } = usePermissions();
  const { data: examinerData } = useGetExaminerQuery(examinerId);

  const availableTabs = tabsConfig
    .filter((tab) => !tab.hidden)
    .filter((tab) => {
      if (!tab.requiresAnyPermission) {
        return true;
      }
      return tab.requiresAnyPermission.some((perm) => permissions.includes(perm));
    })
    .filter((tab) => {
      if (!tab.requiresAnyRole) {
        return true;
      }
      return tab.requiresAnyRole.some((role) => examinerData?.assignedRolesIds.includes(role));
    });

  const lastSegment = location.pathname.split('/').findLast(Boolean);
  const activeIndex = availableTabs.findIndex((tab) => tab.path === lastSegment);

  const panes = availableTabs.map((tab) => ({
    menuItem: { key: tab.path, ...tab.menuItem },
    render: () => <Outlet />,
  }));

  return (
    <S.FlexTab
      $flex={flex}
      panes={panes}
      menu={{ pointing: true, widths: availableTabs.length }}
      activeIndex={activeIndex === -1 ? 0 : activeIndex}
      onTabChange={(_, data) => {
        if (data.activeIndex !== undefined) {
          void navigate(availableTabs[Number(data.activeIndex)].path);
        }
      }}
    />
  );
};

const S = {
  FlexTab: styled(Tab, { shouldForwardProp: (prop) => prop !== '$flex' })`
    ${({ $flex }) =>
      $flex &&
      `
        & .ui.pointing.item.menu {
          display: flex;
          justify-content: flex-start;
          .item {
            flex-grow: 1;
            width: auto;
          }
        }
      `}
  `,
};
